(function () {
  'use strict';

  let lastSyncedVariantId = null;
  let isSyncing = false;
  let filterLinkedOptionsFn = null;

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const dollars = (Number(cents) / 100).toFixed(2);
    return `$ ${dollars}`;
  }

  // Simple Bundles options can carry a surcharge ("S2 Pro Work/Scene (+$114.00)", stored as
  // data-price-diff). The cart charges it, but the PDP price stayed at the base price. Add
  // the selected surcharges to the displayed price. When the product has EPA addons,
  // fcd-spice-addons.js owns the price and adds these in through the global below.
  const OPTION_SELECT_SEL = '.custom-product-right select[name^="properties["]';
  const PRICE_SEL = '.custom-product-right .product-price span.price';

  function getOptionSurchargeCents() {
    let total = 0;
    document.querySelectorAll(OPTION_SELECT_SEL).forEach((select) => {
      const diff = Number(select.selectedOptions[0]?.dataset.priceDiff);
      if (diff > 0) total += Math.round(diff * 100);
    });
    return total;
  }
  window.fcdSimpleBundlesSurchargeCents = getOptionSurchargeCents;

  function hasSurchargeOptions() {
    return [...document.querySelectorAll(`${OPTION_SELECT_SEL} option`)].some(
      (option) => Number(option.dataset.priceDiff) > 0
    );
  }

  function epaOwnsPrice() {
    return [...document.querySelectorAll('[data-fcd-spice-addons]')].some((root) => root._fcdSpiceModel);
  }

  function getVariantPriceCents() {
    const id = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value;
    const json = document.querySelector('script[id^="ProductJson-"]');
    if (!id || !json) return null;
    try {
      const variant = (JSON.parse(json.textContent || '{}').variants || []).find((v) => String(v.id) === id);
      return variant && typeof variant.price === 'number' ? variant.price : null;
    } catch (e) {
      return null;
    }
  }

  function updateOptionPrice() {
    if (!hasSurchargeOptions()) return;
    if (epaOwnsPrice()) {
      document.dispatchEvent(new CustomEvent('apollo:product-options-changed'));
      return;
    }
    const base = getVariantPriceCents();
    if (base === null) return;
    const text = formatMoney(base + getOptionSurchargeCents());
    document.querySelectorAll(PRICE_SEL).forEach((el) => {
      if (el.textContent !== text) el.textContent = text;
    });
  }

  // A variant change re-renders the price block with the bare variant price; put the
  // surcharges back. Only writes when the text differs, so its own write doesn't loop.
  function watchOptionPrice() {
    const container = document.querySelector('.custom-product-right');
    if (!container || !hasSurchargeOptions()) return;
    new MutationObserver((mutations) => {
      if (epaOwnsPrice()) return;
      const touchesPrice = mutations.some((m) => {
        const node = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        return node && node.closest && node.closest('.product-price');
      });
      if (touchesPrice) updateOptionPrice();
    }).observe(container, { childList: true, subtree: true, characterData: true });
    updateOptionPrice();
  }

  async function syncVariant(variantId, variantData) {
    if (!variantId) return;
    const vId = String(variantId);
    if (lastSyncedVariantId === vId && !isSyncing) return;
    lastSyncedVariantId = vId;

    const forms = document.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
    forms.forEach((form) => {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput && idInput.value !== vId) {
        idInput.value = vId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    const refVariantInputs = document.querySelectorAll('[ref="variantId"]');
    refVariantInputs.forEach((input) => {
      if (input.value !== vId) {
        input.value = vId;
      }
    });

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') !== vId) {
        url.searchParams.set('variant', vId);
        window.history.replaceState({}, '', url.toString());
      }
    } catch (e) {
      console.warn('[SimpleBundlesBridge] Failed to update URL:', e);
    }

    if (variantData) {
      if (variantData.sku !== undefined) {
        const skuContainers = document.querySelectorAll('.product-sku');
        skuContainers.forEach((el) => {
          if (variantData.sku) {
            el.textContent = `SKU: ${variantData.sku}`;
            el.style.display = '';
          } else {
            el.style.display = 'none';
          }
        });
      }
      if (typeof variantData.price === 'number') {
        const priceEls = document.querySelectorAll('.product-price .price');
        priceEls.forEach((el) => {
          el.textContent = formatMoney(variantData.price);
        });

        const comparePriceEls = document.querySelectorAll('.product-price .compare-at-price');
        if (variantData.compare_at_price && variantData.compare_at_price > variantData.price) {
          comparePriceEls.forEach((el) => {
            el.textContent = formatMoney(variantData.compare_at_price);
            el.style.display = '';
          });
          document.querySelectorAll('.product-price .price__sale').forEach((el) => el.classList.remove('price__hidden'));
          document.querySelectorAll('.product-price .price__regular').forEach((el) => el.classList.add('price__hidden'));
        } else {
          comparePriceEls.forEach((el) => {
            el.style.display = 'none';
          });
        }
      }
    }

    const spiceRoots = document.querySelectorAll('[data-fcd-spice-addons]');
    spiceRoots.forEach((root) => {
      if (variantData && typeof variantData.price === 'number') {
        if (root._fcdSpiceConfig) {
          root._fcdSpiceConfig.basePriceCents = variantData.price;
        }
      }
      root.dispatchEvent(new CustomEvent('change', { bubbles: true }));
    });

    isSyncing = true;
    try {
      const sectionEl = document.querySelector('.custom-product-container[data-section-id]') ||
                        document.querySelector('[data-section-id]');
      const sectionId = sectionEl?.dataset?.sectionId || 'main';
      const requestUrl = `${window.location.pathname}?variant=${vId}&section_id=${sectionId}`;

      const response = await fetch(requestUrl);
      if (response.ok) {
        const text = await response.text();
        // A newer variant was picked while this request was in flight; its own sync will render.
        if (lastSyncedVariantId !== vId) return;
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const newGallery = doc.querySelector('media-gallery');
        const currentGallery = document.querySelector('media-gallery');
        if (newGallery && currentGallery) {
          currentGallery.replaceWith(newGallery);
        }

        const newPrice = doc.querySelector('.product-price');
        const currentPrice = document.querySelector('.product-price');
        if (newPrice && currentPrice) {
          currentPrice.innerHTML = newPrice.innerHTML;
        }

        const newSku = doc.querySelector('.product-sku');
        const currentSku = document.querySelector('.product-sku');
        if (newSku && currentSku) {
          currentSku.innerHTML = newSku.innerHTML;
          currentSku.style.display = newSku.style.display;
        }

        const newTerms = doc.querySelector('.payment-terms');
        const currentTerms = document.querySelector('.payment-terms');
        if (newTerms && currentTerms) {
          currentTerms.replaceWith(newTerms);
        }
      }
    } catch (err) {
      console.warn('[SimpleBundlesBridge] Section rendering sync error:', err);
    } finally {
      isSyncing = false;
    }

    document.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant: variantData || { id: vId } }
    }));
  }

  function setupSimpleBundlesProxy() {
    function attachVariantTrap(sb) {
      if (!sb || sb._variantTrapAttached) return;
      sb._variantTrapAttached = true;

      let _variant = sb.currentProductVariant;

      if (_variant && _variant.id) {
        lastSyncedVariantId = String(_variant.id);
      }

      try {
        Object.defineProperty(sb, 'currentProductVariant', {
          get() {
            return _variant;
          },
          set(newVariant) {
            _variant = newVariant;
            if (newVariant && newVariant.id) {
              syncVariant(newVariant.id, newVariant);
            }
          },
          configurable: true,
          enumerable: true
        });
      } catch (e) {
        console.warn('[SimpleBundlesBridge] Could not define property on window.SimpleBundles:', e);
      }
    }

    if (window.SimpleBundles) {
      attachVariantTrap(window.SimpleBundles);
    } else {
      let _simpleBundlesInstance = undefined;
      try {
        Object.defineProperty(window, 'SimpleBundles', {
          get() {
            return _simpleBundlesInstance;
          },
          set(val) {
            _simpleBundlesInstance = val;
            attachVariantTrap(val);
          },
          configurable: true,
          enumerable: true
        });
      } catch (e) {
        console.warn('[SimpleBundlesBridge] Could not trap window.SimpleBundles:', e);
      }
    }
  }

  function setupDomListeners() {
    document.addEventListener('change', function (e) {
      const target = e.target;
      if (!target) return;

      // After the variant sync below, which can re-render the price block.
      if (target.matches && target.matches(OPTION_SELECT_SEL)) setTimeout(updateOptionPrice, 60);

      // EPA addon inputs (the FCD addon UI and the hidden native Spice block it keeps in sync)
      // sit inside .custom-app-wrapper too, but they aren't Simple Bundles options. Treating
      // them as one re-applied SimpleBundles.currentProductVariant — the variant from page load,
      // since the pills don't update it — so ticking an addon silently switched the PDP back
      // to the first variant and that's what went into the cart.
      if (target.closest('[data-fcd-spice-addons], #spiceaddonsappid, .spice-spa-addon-layouts')) return;

      const isBundleChange = target.closest(
        '[data-simple-bundles-container], .simple-bundles-infinite-options, [id*="simple-bundles"], .custom-app-wrapper'
      );
      if (isBundleChange) {
        setTimeout(() => {
          if (window.SimpleBundles && window.SimpleBundles.currentProductVariant) {
            syncVariant(window.SimpleBundles.currentProductVariant.id, window.SimpleBundles.currentProductVariant);
          }
        }, 50);
        return;
      }

      if (target.matches('.selector-wrapper select, select[id^="SingleOptionSelector-"]')) {
        handleNativeSelectorChange();
        return;
      }

      if (target.matches('input[name="id"], select[name="id"]')) {
        if (target.value && target.value !== lastSyncedVariantId) {
          syncVariant(target.value);
        }
      }
    }, true);
  }

  function setupLinkedOptions() {
    const sectionEl = document.querySelector('.custom-product-container[data-section-id]') ||
                      document.querySelector('[data-section-id]');
    const sectionId = sectionEl?.dataset?.sectionId || 'main';
    const productJsonEl = document.getElementById('ProductJson-' + sectionId) ||
                          document.querySelector('script[type="application/json"][data-product-json]');
    if (!productJsonEl) return null;

    let product;
    try {
      product = JSON.parse(productJsonEl.textContent);
    } catch (e) {
      return null;
    }

    const selects = Array.from(document.querySelectorAll('.selector-wrapper select, select[id^="SingleOptionSelector-"]'));
    if (selects.length === 0) return null;

    selects.forEach((select) => {
      if (!select._allOptions) {
        select._allOptions = Array.from(select.options).map((opt) => ({
          value: opt.value,
          text: opt.textContent
        }));
      }
    });

    function filterOptions() {
      for (let i = 1; i < selects.length; i++) {
        const prevValues = selects.slice(0, i).map((s) => s.value);
        const validVariants = product.variants.filter((v) =>
          prevValues.every((val, idx) => v.options[idx] === val)
        );
        const validOptionValues = new Set(validVariants.map((v) => v.options[i]));
        const select = selects[i];
        const currentVal = select.value;

        select.innerHTML = '';
        const availableOptions = select._allOptions.filter((opt) => validOptionValues.has(opt.value));
        availableOptions.forEach((opt) => {
          const optEl = document.createElement('option');
          optEl.value = opt.value;
          optEl.textContent = opt.text;
          select.appendChild(optEl);
        });

        if (validOptionValues.has(currentVal)) {
          select.value = currentVal;
        } else if (availableOptions.length > 0) {
          select.value = availableOptions[0].value;
        }
      }

      const currentSelections = selects.map((s) => s.value);
      const matchingVariant = product.variants.find((v) =>
        v.options.every((opt, idx) => opt === currentSelections[idx])
      );
      if (matchingVariant) {
        syncVariant(matchingVariant.id, matchingVariant);
      }
    }

    filterOptions();
    return filterOptions;
  }

  function handleNativeSelectorChange() {
    if (filterLinkedOptionsFn) {
      filterLinkedOptionsFn();
    } else {
      filterLinkedOptionsFn = setupLinkedOptions();
    }
  }

  function setupInputObserver() {
    const idInput = document.querySelector('form[action*="/cart/add"] input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]');
    if (!idInput) return;

    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const newId = idInput.value;
          if (newId && newId !== lastSyncedVariantId) {
            syncVariant(newId);
          }
        }
      });
    });

    observer.observe(idInput, { attributes: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupSimpleBundlesProxy();
      setupDomListeners();
      setupInputObserver();
      filterLinkedOptionsFn = setupLinkedOptions();
      watchOptionPrice();
    });
  } else {
    setupSimpleBundlesProxy();
    setupDomListeners();
    setupInputObserver();
    filterLinkedOptionsFn = setupLinkedOptions();
    watchOptionPrice();
  }
})();
